import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import debugFactory from 'debug';
import 'focus-visible';
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'tippy.js/dist/tippy.css';

import { ResizeSender, createIpc, detectParentOrigin } from './bootstrap';
import { IPCEvents } from './bootstrap/types';
import { initErrorTracking } from './commons/services/error-tracking-service';
import { TranslationsPapi } from './commons/utils/translations/translate';
import { GlobalStateMaster } from './global-state';
import './utils/intl-polyfill';
import { IpcContext } from './utils/ipc-utils';
import { ExpandWidgetContext } from './widget-expander';
import { Widget } from './widget/widget';

const debug = debugFactory('ingrid:bootstrap');

debug('Started checkout');

document.body.style.margin = '0';
document.body.style.overflow = 'hidden';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

export const ipc = createIpc<IPCEvents>({
  window: window.parent,
  origin: detectParentOrigin(),
});

new ResizeSender(document.body, ipc);

initErrorTracking();

const App: React.FC = () => {
  const [expandedBy, setExpandedBy] = React.useState(0);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('entry', 'checkout');
      }}
    >
      <ExpandWidgetContext.Provider value={{ expandedBy, setExpandedBy }}>
        <QueryClientProvider client={queryClient}>
          <IpcContext.Provider value={ipc}>
            <GlobalStateMaster>
              <TranslationsPapi>
                <Widget />
              </TranslationsPapi>
            </GlobalStateMaster>
          </IpcContext.Provider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ExpandWidgetContext.Provider>
    </Sentry.ErrorBoundary>
  );
};

const container = document.getElementById('checkout');
createRoot(container as HTMLElement).render(<App />);
